import * as React from "react";

import { useAddinsLinksToFactsetWorkstationEnabled } from "../../featureFlags/useAddinsLinksToFactsetWorkstationEnabled";
import type { SkillsController } from "../hooks/useSkillsController";
import { MAX_SELECTED_SKILLS, type SkillSummary } from "../types";
import { SkillDetailView } from "./SkillDetailView";
import { SkillExploreView } from "./SkillExploreView";

export interface SkillsViewProps {
  open: boolean;
  controller: SkillsController;
  manageSkillsUrl?: string;
  onBack: () => void;
  onClose: () => void;
  onTryInChat: (skill: SkillSummary) => void;
}

export const SkillsView: React.FC<SkillsViewProps> = ({
  open,
  controller,
  manageSkillsUrl,
  onBack,
  onClose,
  onTryInChat,
}) => {
  // Read before the early return so the hook order stays stable across renders.
  const workstationLinksEnabled = useAddinsLinksToFactsetWorkstationEnabled();

  if (!open || !controller.enabled) return null;

  // The Explore header renders the link on URL presence alone, so the
  // Workstation rollout gate has to be applied here rather than in the leaf.
  const manageSkillsHref = workstationLinksEnabled ? manageSkillsUrl : undefined;

  const closeView = () => {
    controller.closeDetail();
    onClose();
  };

  if (controller.detailSummary) {
    const selected = controller.selectedSkills.some(
      (skill) =>
        skill.id === controller.detailSummary?.id || skill.slug === controller.detailSummary?.slug
    );
    const atLimit = controller.selectionAtLimit && !selected;

    return (
      <SkillDetailView
        open
        skill={controller.detail}
        status={controller.detailStatus}
        selected={selected}
        tryInChatDisabled={atLimit}
        tryInChatDisabledReason={
          atLimit ? `You can select up to ${MAX_SELECTED_SKILLS} Skills.` : undefined
        }
        errorMessage={controller.detailError?.message ?? "Unable to load this Skill."}
        onBack={controller.closeDetail}
        onClose={closeView}
        onTryInChat={(skill) => {
          controller.closeDetail();
          onTryInChat(skill);
        }}
      />
    );
  }

  return (
    <SkillExploreView
      open
      skills={controller.skills}
      search={controller.browseSearch}
      sourceFilter={controller.browseSourceFilter}
      status={controller.status}
      manageSkillsUrl={manageSkillsHref}
      errorMessage={controller.catalogError?.message ?? "Unable to load Skills."}
      scrollTop={controller.browseScrollTop}
      onScrollTopChange={controller.setBrowseScrollTop}
      onSearchChange={controller.setBrowseSearch}
      onSourceFilterChange={controller.setBrowseSourceFilter}
      onOpenDetail={controller.openDetail}
      onBack={onBack}
      onClose={closeView}
    />
  );
};
